/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';
import dayjs from 'dayjs';

import { withOffset } from 'Context/LayoutContext';
import { useConfig } from 'Context/ConfigContext';
import { getTextColorByContrast } from 'Util/color';

import { QUIZ_VARIANTS } from '../../constant';
import { Box, Flex, Image as BaseImage, Text, ButtonFilled } from '../../../Base';
import QuizDetails, { QUIZ_DETAIL_VARIANTS } from '../../Components/QuizDetails';
import ReportTable from '../../Components/ReportTable';

const Image = styled(BaseImage)`
  display: none;
  @media screen and (min-height: 600px) {
    display: block;
  }
`;

// 64 base for sticky header + 12 paddingY
const HEADER_HEIGHT = 76;
// 91 base for sticky footer
const FOOTER_HEIGHT = 91;

// header + footer + offset otherwise is blank space which is quiz landing should render
const DefaultQuizLandingWrapper = styled((props) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent={'center'}
    p={[2, 4]}
    width={1}
    {...props}
  />
))`
  ${({ theme }) => css`
    overflow: auto;
    height: calc(100vh - ${HEADER_HEIGHT + FOOTER_HEIGHT + theme.space.APP_TOP_OFFSET}px);
  `}
`;

const GradedExamLandingWrapper = styled((props) => (
  // This will show all the previous quiz results, resulting in a very long scrollable container
  // <Flex flexDirection="column" alignItems="center" p={[2, 3]} width={1} {...props} />
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    p={[0, 2, 3]}
    width={1}
    {...props}
  />
))`
  overflow: auto;
  height: 100%;
`;

const QuizLandingWrapper = ({ variant, children }) => {
  const { space } = useTheme();
  switch (variant) {
    case QUIZ_VARIANTS.GRADED_EXAM:
      return <GradedExamLandingWrapper>{children}</GradedExamLandingWrapper>;
    default:
      return (
        <Box
          bg="quizLandingBackground"
          height="100vh"
          pt={`${space?.APP_TOP_OFFSET + HEADER_HEIGHT}px`}
        >
          <DefaultQuizLandingWrapper>{children}</DefaultQuizLandingWrapper>
        </Box>
      );
  }
};

const QuizLandingWrapperMemo = React.memo(QuizLandingWrapper, (prevProps, nextProps) => {
  return prevProps.variant === nextProps.variant && prevProps.children === nextProps.children;
});

const Landing = ({
  title,
  variant,
  onStartQuiz,
  totalRound,
  remainingRound,
  timeLimit,
  fullScore,
  passingScore,
  resultOptions,
  onSeeReport,
  reports,
  onGoToNextSubsection,
  disableStartQuizButton,
  openCourseReviewModal,
  isLastSubsectionInCourse,
  onExitClassroom,
}) => {
  const isNotFirstRound = reports && reports.length > 0;
  const hasReachedMaxAttempts = remainingRound <= 0;
  const { colors } = useTheme();
  const {
    classroom: { quiz },
  } = useConfig();
  const { imagePrefix = '', imageSuffix = 'svg' } = quiz;

  const ImageBlock = useMemo(() => {
    switch (variant) {
      case QUIZ_VARIANTS.GRADED_EXAM:
        return (
          <Image src={`/assets/images/quiz/${imagePrefix}quiz-landing.${imageSuffix}`} mb={6} />
        );
      default:
        return <Image src={'/assets/images/quiz/quiz-landing.svg'} width={'97px'} mb={6} />;
    }
  }, [variant]);

  const QuizTypeBlock = useMemo(() => {
    switch (variant) {
      case QUIZ_VARIANTS.GRADED_EXAM:
        return null;
      case QUIZ_VARIANTS.OFFICIAL_EXAM:
        return (
          <Text textStyle={'heading4'} color={'white'} mb={3}>
            ข้อสอบ
          </Text>
        );
      default:
        return (
          <Text textStyle={'heading4'} color={'white'} mb={3}>
            Quiz
          </Text>
        );
    }
  }, [variant]);

  const TitleBlock = useMemo(() => {
    switch (variant) {
      case QUIZ_VARIANTS.GRADED_EXAM:
        return (
          <Text
            textStyle={{ _: 'heading5', sm: 'heading3' }}
            textAlign={'center'}
            mb={{ _: 3, md: 7 }}
          >
            {title}
          </Text>
        );
      default:
        return (
          <Text textStyle={'heading5'} textAlign={'center'} color={'white'} mb={7}>
            {title}
          </Text>
        );
    }
  }, [variant]);

  const DetailsBlock = useMemo(() => {
    switch (variant) {
      case QUIZ_VARIANTS.GRADED_EXAM:
        return (
          <QuizDetails
            variant={QUIZ_DETAIL_VARIANTS.LANDING}
            data={{ passingScore, fullScore, timeLimit }}
          />
        );
      case QUIZ_VARIANTS.OFFICIAL_EXAM: {
        const { isShow, showAfter } = resultOptions;
        const isAfterDate = showAfter ? dayjs().isAfter(dayjs(showAfter)) : true;
        const isShowResultNow = isShow && isAfterDate;
        return (
          <Text color={'white'} mb={3}>
            {!isShowResultNow
              ? isShow
                ? `แสดงผลสอบและเฉลยหลังจากวันที่ ${dayjs(showAfter).format('DD MMM YY / HH:mm')} `
                : 'ไม่แสดงผลสอบและเฉลย'
              : ''}
          </Text>
        );
      }
      default:
        return null;
    }
  }, [variant]);

  const RemainingRoundText = useMemo(() => {
    const dataTestIdPrefix = 'quiz-remaining-round';

    switch (variant) {
      case QUIZ_VARIANTS.GRADED_EXAM:
        return (
          <Text
            textStyle={'body2'}
            fontWeight={'normal'}
            textAlign={'center'}
            mb={3}
            data-testid={`${dataTestIdPrefix}-graded-exam`}
          >
            {hasReachedMaxAttempts
              ? `คุณได้ทำแบบทดสอบนี้ครบ ${totalRound} ครั้งแล้ว`
              : `ทำได้อีก ${remainingRound}/${totalRound} ครั้ง`}
          </Text>
        );
      case QUIZ_VARIANTS.OFFICIAL_EXAM:
        return (
          <Text
            color={'white'}
            textAlign={'center'}
            mb={3}
            data-testid={`${dataTestIdPrefix}-official-exam`}
          >
            {hasReachedMaxAttempts
              ? `คุณได้ทำข้อสอบนี้ครบ ${totalRound} ครั้งแล้ว`
              : `ทำได้อีก ${remainingRound}/${totalRound} ครั้ง`}
          </Text>
        );
      default:
        return (
          <Text
            color={'white'}
            textAlign={'center'}
            mb={3}
            data-testid={`${dataTestIdPrefix}-default`}
          >
            {hasReachedMaxAttempts
              ? `คุณได้ทำแบบฝึกหัดนี้ครบ ${totalRound} ครั้งแล้ว`
              : `ทำได้อีก ${remainingRound}/${totalRound} ครั้ง`}
          </Text>
        );
    }
  }, [variant]);

  const StartQuizButton = useMemo(() => {
    const dataTestIdPrefix = 'btn-quiz-start';

    switch (variant) {
      case QUIZ_VARIANTS.GRADED_EXAM:
        return (
          <Flex justifyContent="center">
            <ButtonFilled
              width={1}
              maxWidth={370}
              fontWeight={'bold'}
              height={40}
              borderRadius={20}
              onClick={onStartQuiz}
              disabled={disableStartQuizButton}
              textColor={getTextColorByContrast(colors.primary, 'text', 'white')}
              pt={2}
              data-testid={`${dataTestIdPrefix}-graded-exam`}
            >
              {isNotFirstRound ? 'ทำแบบทดสอบอีกครั้ง' : 'เริ่มทำแบบทดสอบ'}
            </ButtonFilled>
          </Flex>
        );
      case QUIZ_VARIANTS.OFFICIAL_EXAM:
        return (
          <Flex justifyContent="center">
            <ButtonFilled
              variant={'default'}
              color={'white'}
              textColor={'text'}
              fontWeight={600}
              width={'100%'}
              maxWidth={595}
              onClick={onStartQuiz}
              disabled={disableStartQuizButton}
              data-testid={`${dataTestIdPrefix}-official-exam`}
            >
              {isNotFirstRound ? 'ทำข้อสอบอีกครั้ง' : 'เริ่มทำข้อสอบ'}
            </ButtonFilled>
          </Flex>
        );
      default:
        return (
          <Flex justifyContent="center">
            <ButtonFilled
              variant={'default'}
              color={'white'}
              textColor={'text'}
              fontWeight={600}
              width={'100%'}
              maxWidth={595}
              onClick={onStartQuiz}
              disabled={disableStartQuizButton}
              data-testid={`${dataTestIdPrefix}-default`}
            >
              {isNotFirstRound ? 'ทำแบบฝึกหัดอีกครั้ง' : 'เริ่มทำแบบฝึกหัด'}
            </ButtonFilled>
          </Flex>
        );
    }
  }, [variant]);

  const NextSubsectionButton = useMemo(() => {
    switch (variant) {
      case QUIZ_VARIANTS.GRADED_EXAM:
        if (isLastSubsectionInCourse) {
          return (
            <Flex justifyContent="center">
              <ButtonFilled
                borderRadius={100}
                width={1}
                maxWidth={343}
                onClick={openCourseReviewModal}
                py={2}
              >
                รีวิวคอร์สเรียน
              </ButtonFilled>
            </Flex>
          );
        }
        return (
          <Flex justifyContent="center">
            <ButtonFilled
              borderRadius={100}
              width={1}
              maxWidth={343}
              onClick={onGoToNextSubsection}
              py={2}
            >
              เรียนบทต่อไป
            </ButtonFilled>
          </Flex>
        );
      case QUIZ_VARIANTS.OFFICIAL_EXAM: {
        return (
          <Flex justifyContent="center">
            <ButtonFilled
              variant={'default'}
              color={'white'}
              textColor={'text'}
              fontWeight={600}
              width={'100%'}
              maxWidth={595}
              onClick={isLastSubsectionInCourse ? onExitClassroom : onGoToNextSubsection}
            >
              {isLastSubsectionInCourse ? 'กลับสู่หน้าคอร์สของฉัน' : 'เรียนบทต่อไป'}
            </ButtonFilled>
          </Flex>
        );
      }
      default:
        return (
          <Flex justifyContent="center">
            <ButtonFilled
              variant={'default'}
              color={'white'}
              textColor={'text'}
              fontWeight={600}
              width={'100%'}
              maxWidth={595}
              onClick={isLastSubsectionInCourse ? onExitClassroom : onGoToNextSubsection}
            >
              {isLastSubsectionInCourse ? 'กลับสู่หน้าคอร์สของฉัน' : 'เรียนบทต่อไป'}
            </ButtonFilled>
          </Flex>
        );
    }
  }, [variant]);

  return (
    <QuizLandingWrapperMemo variant={variant}>
      {ImageBlock}
      {QuizTypeBlock}
      {TitleBlock}
      {DetailsBlock}
      {isNotFirstRound && (
        <ReportTable
          data-testid="quiz-report-table"
          reports={reports}
          onSeeReport={onSeeReport}
          mb={{ _: 2, md: 6 }}
          minHeight="fit-content"
          passingScore={passingScore}
          variant={variant}
        />
      )}
      <Box
        mt={{ _: 2, md: 6 }}
        width={'100%'}
        pb={{ _: 0, md: variant === QUIZ_VARIANTS.GRADED_EXAM ? 7 : 0 }}
      >
        {RemainingRoundText}
        {hasReachedMaxAttempts ? NextSubsectionButton : StartQuizButton}
      </Box>
    </QuizLandingWrapperMemo>
  );
};

Landing.propTypes = {
  title: PropTypes.string,
  totalRound: PropTypes.number,
  remainingRound: PropTypes.number,
  onStartQuiz: PropTypes.func,
  ...ReportTable.propTypes,
};
Landing.defaultProps = {
  onStartQuiz: () => {},
  ...ReportTable.defaultProps,
};

export default withOffset(Landing);
